import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { 
  ArrowLeft, Edit2, User, Mail, Phone, Globe, DollarSign, 
  Award, TrendingUp, Briefcase, Target, BookOpen, Shield, 
  Star, Clock, CheckCircle, Zap, Code, Copy, Check, Settings, Inbox
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AIActorView() {
  const { actorId } = useParams();
  const navigate = useNavigate();
  const [actor, setActor] = useState(null);
  const [coachingNotes, setCoachingNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [embedScript, setEmbedScript] = useState("");
  const [copied, setCopied] = useState(false);
  const [branding, setBranding] = useState(null);

  useEffect(() => {
    fetchActorDetails();
    fetchCoachingNotes();
    fetchBrandingSettings();
  }, [actorId]);

  useEffect(() => {
    if (actor && branding) {
      generateEmbedScript();
    }
  }, [actor, branding]);

  const fetchActorDetails = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/ai/actors`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const foundActor = response.data.find(a => a.id === actorId);
      if (foundActor) {
        setActor(foundActor);
      } else {
        toast.error("AI Actor not found");
        navigate("/ai/actors");
      }
    } catch (error) {
      toast.error("Failed to fetch AI Actor details");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCoachingNotes = async () => {
    try {
      setLoadingNotes(true);
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/admin/ai-actors/${actorId}/coaching-notes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCoachingNotes(response.data.coaching_notes || []);
    } catch (error) {
      console.error("Failed to fetch coaching notes", error);
      setCoachingNotes([]);
    } finally {
      setLoadingNotes(false);
    }
  };

  const deleteCoachingNote = async (noteId) => {
    if (!window.confirm("Are you sure you want to delete this coaching note?")) {
      return;
    }
    
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/admin/ai-actors/${actorId}/coaching-notes/${noteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Coaching note deleted");
      fetchCoachingNotes();
    } catch (error) {
      toast.error("Failed to delete coaching note");
      console.error(error);
    }
  };

  const fetchBrandingSettings = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/settings/branding`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBranding(response.data);
    } catch (error) {
      console.error("Failed to fetch branding settings", error);
      // Set default branding if fetch fails
      setBranding({
        brand_name: "AI Assistant",
        primary_color: "#4F46E5",
        secondary_color: "#2563EB",
        accent_color: "#3B82F6"
      });
    }
  };

  const generateEmbedScript = () => {
    const brandName = branding?.brand_name || "AI Assistant";
    const primaryColor = branding?.primary_color || "#4F46E5";
    const secondaryColor = branding?.secondary_color || "#2563EB";
    
    // Fix brand logo URL to use /api/uploads/ if needed
    let brandLogoUrl = '';
    if (branding?.brand_logo) {
      const logoPath = branding.brand_logo;
      if (logoPath.startsWith('/uploads/') && !logoPath.startsWith('/api/uploads/')) {
        brandLogoUrl = BACKEND_URL + '/api' + logoPath;
      } else if (logoPath.startsWith('/api/uploads/')) {
        brandLogoUrl = BACKEND_URL + logoPath;
      } else {
        brandLogoUrl = BACKEND_URL + branding.brand_logo;
      }
    }
    
    const script = `<!--Start of ${brandName} AI Chat Widget-->
<script type="text/javascript">
(function() {
  var config = {
    actorId: '${actorId}',
    actorName: '${actor?.name || 'AI Assistant'}',
    apiUrl: '${BACKEND_URL}/api',
    brandName: '${brandName}',
    primaryColor: '${primaryColor}',
    secondaryColor: '${secondaryColor}',
    brandLogo: '${brandLogoUrl}'
  };
  
  var script = document.createElement('script');
  script.src = '${BACKEND_URL}/api/widget/chat-widget.js';
  script.async = true;
  script.onload = function() {
    if (window.initAIChatWidget) {
      window.initAIChatWidget(config);
    }
  };
  document.head.appendChild(script);
  
  var link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = '${BACKEND_URL}/api/widget/chat-widget.css';
  document.head.appendChild(link);
})();
</script>
<!--End of ${brandName} AI Chat Widget-->`;
    
    setEmbedScript(script);
  };

  const copyEmbedScript = () => {
    navigator.clipboard.writeText(embedScript);
    setCopied(true);
    toast.success("Embed script copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-muted-foreground">Loading AI Actor details...</p>
      </div>
    );
  }

  if (!actor) {
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-muted-foreground">AI Actor not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          {/* Navigation buttons hidden as per requirement */}
        </div>

        {/* Profile Header */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-start gap-6">
              {/* Profile Image */}
              <div className="flex-shrink-0">
                {actor.profile_image ? (
                  <img
                    src={`${BACKEND_URL}/api/uploads/actor_images/${actor.profile_image}`}
                    alt={actor.name}
                    className="w-32 h-32 rounded-full object-cover border-4 border-indigo-100"
                    onError={(e) => {
                      console.error('Image failed to load:', e.target.src);
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div 
                  className="w-32 h-32 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center"
                  style={{ display: actor.profile_image ? 'none' : 'flex' }}
                >
                  <User className="w-16 h-16 text-white" />
                </div>
              </div>

              {/* Basic Info */}
              <div className="flex-1">
                <div className="flex items-start justify-between">
                  <div>
                    <h1 className="text-3xl font-bold text-slate-900">{actor.name}</h1>
                    {actor.experience_level && (
                      <p className="text-lg text-muted-foreground mt-1">{actor.experience_level}</p>
                    )}
                  </div>
                  <Badge variant={actor.is_public ? "default" : "secondary"} className="ml-4">
                    {actor.is_public ? "Public" : "Private"}
                  </Badge>
                </div>

                <div className="flex flex-wrap gap-4 mt-4">
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    <span>{actor.email}</span>
                  </div>
                  {actor.phone && (
                    <div className="flex items-center gap-2 text-sm">
                      <Phone className="w-4 h-4 text-muted-foreground" />
                      <span>{actor.phone}</span>
                    </div>
                  )}
                  {actor.availability_status && (
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span className="text-green-700">{actor.availability_status}</span>
                    </div>
                  )}
                  {actor.response_time && (
                    <div className="flex items-center gap-2 text-sm">
                      <Clock className="w-4 h-4 text-muted-foreground" />
                      <span>{actor.response_time}</span>
                    </div>
                  )}
                </div>

                {/* Verified Badges */}
                {actor.verified_badges && actor.verified_badges.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-4">
                    {actor.verified_badges.map((badge, index) => (
                      <Badge key={index} variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                        <Shield className="w-3 h-3 mr-1" />
                        {badge}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* About & Background */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {actor.about && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="w-5 h-5 text-indigo-600" />
                  About
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-700 whitespace-pre-wrap">{actor.about}</p>
              </CardContent>
            </Card>
          )}

          {actor.background && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-indigo-600" />
                  Background
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-700 whitespace-pre-wrap">{actor.background}</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Work Profile */}
        {actor.work_profile && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-indigo-600" />
                Professional Experience
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-700 whitespace-pre-wrap">{actor.work_profile}</p>
            </CardContent>
          </Card>
        )}

        {/* Skills & Expertise */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Skills */}
          {actor.skills && actor.skills.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="w-5 h-5 text-indigo-600" />
                  Skills
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {actor.skills.map((skill, index) => (
                    <Badge key={index} variant="secondary">
                      {skill}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Industries Served */}
          {actor.industries_served && actor.industries_served.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="w-5 h-5 text-indigo-600" />
                  Industries Served
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {actor.industries_served.map((industry, index) => (
                    <Badge key={index} variant="outline">
                      {industry}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Pricing */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-600" />
              Pricing
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {actor.hourly_rate && (
              <div>
                <p className="text-sm text-muted-foreground">Hourly Rate</p>
                <p className="text-2xl font-bold text-green-600">${actor.hourly_rate}/hour</p>
              </div>
            )}

            {actor.packages && actor.packages.length > 0 && (
              <div>
                <p className="text-sm text-muted-foreground mb-3">Packages</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {actor.packages.map((pkg, index) => (
                    <div key={index} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                      <h3 className="font-semibold text-lg">{pkg.name}</h3>
                      <p className="text-2xl font-bold text-green-600 mt-2">${pkg.price}</p>
                      <p className="text-sm text-muted-foreground mt-2">{pkg.description}</p>
                      {pkg.features && pkg.features.length > 0 && (
                        <ul className="mt-3 space-y-1">
                          {pkg.features.map((feature, idx) => (
                            <li key={idx} className="text-sm flex items-start gap-2">
                              <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Performance Metrics */}
        {(actor.jobs_completed || actor.success_rate || actor.revenue_generated || actor.time_saved_stats) && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Award className="w-5 h-5 text-amber-600" />
                Performance Metrics
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {actor.jobs_completed && (
                  <div>
                    <p className="text-sm text-muted-foreground">Jobs Completed</p>
                    <p className="text-2xl font-bold text-slate-900">{actor.jobs_completed}</p>
                  </div>
                )}
                {actor.success_rate && (
                  <div>
                    <p className="text-sm text-muted-foreground">Success Rate</p>
                    <p className="text-2xl font-bold text-green-600">{actor.success_rate}%</p>
                  </div>
                )}
                {actor.revenue_generated && (
                  <div>
                    <p className="text-sm text-muted-foreground">Revenue Generated</p>
                    <p className="text-lg font-bold text-slate-900">{actor.revenue_generated}</p>
                  </div>
                )}
                {actor.time_saved_stats && (
                  <div>
                    <p className="text-sm text-muted-foreground">Time Saved</p>
                    <p className="text-lg font-bold text-slate-900">{actor.time_saved_stats}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Credentials */}
        {actor.credentials && actor.credentials.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Award className="w-5 h-5 text-indigo-600" />
                Credentials & Certifications
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {actor.credentials.map((credential, index) => (
                  <li key={index} className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span>{credential}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Responsibilities */}
        {actor.responsibilities && actor.responsibilities.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-indigo-600" />
                Key Responsibilities
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {actor.responsibilities.map((resp, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-indigo-600 flex-shrink-0 mt-0.5" />
                    <span>{resp}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Languages */}
        {actor.languages && actor.languages.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="w-5 h-5 text-indigo-600" />
                Languages
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {actor.languages.map((language, index) => (
                  <Badge key={index} variant="outline">
                    {language}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Projects */}
        {actor.projects && actor.projects.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-indigo-600" />
                Past Projects
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {actor.projects.map((project, index) => (
                  <div key={index} className="border-l-4 border-indigo-400 pl-4 py-2">
                    <h4 className="font-semibold text-lg">{project.title || `Project ${index + 1}`}</h4>
                    {project.description && (
                      <p className="text-sm text-slate-600 mt-1">{project.description}</p>
                    )}
                    {project.technologies && (
                      <p className="text-sm text-muted-foreground mt-2">
                        <strong>Technologies:</strong> {project.technologies}
                      </p>
                    )}
                    {project.url && (
                      <a 
                        href={project.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm text-indigo-600 hover:underline mt-2 inline-block"
                      >
                        View Project →
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Case Studies */}
        {actor.case_studies && actor.case_studies.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-indigo-600" />
                Case Studies
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {actor.case_studies.map((caseStudy, index) => (
                  <div key={index} className="border rounded-lg p-4">
                    <h4 className="font-semibold text-lg">{caseStudy.title}</h4>
                    {caseStudy.client_industry && (
                      <p className="text-sm text-muted-foreground">Industry: {caseStudy.client_industry}</p>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                      {caseStudy.before && (
                        <div>
                          <p className="text-sm font-semibold text-red-600">Before:</p>
                          <p className="text-sm text-slate-600">{caseStudy.before}</p>
                        </div>
                      )}
                      {caseStudy.after && (
                        <div>
                          <p className="text-sm font-semibold text-green-600">After:</p>
                          <p className="text-sm text-slate-600">{caseStudy.after}</p>
                        </div>
                      )}
                    </div>
                    {caseStudy.results && (
                      <div className="mt-4">
                        <p className="text-sm font-semibold">Results:</p>
                        <p className="text-sm text-slate-600">{caseStudy.results}</p>
                      </div>
                    )}
                    {caseStudy.metrics && (
                      <div className="mt-2">
                        <Badge variant="outline" className="bg-green-50 text-green-700">
                          {caseStudy.metrics}
                        </Badge>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Social Media */}
        {actor.social_media && actor.social_media.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="w-5 h-5 text-indigo-600" />
                Social Media Profiles
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {actor.social_media.map((social, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <Badge variant="outline">{social.platform}</Badge>
                    <a 
                      href={social.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-sm text-indigo-600 hover:underline"
                    >
                      {social.url}
                    </a>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Video Introduction */}
        {actor.video_url && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Star className="w-5 h-5 text-indigo-600" />
                Video Introduction
              </CardTitle>
            </CardHeader>
            <CardContent>
              <a 
                href={actor.video_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-indigo-600 hover:underline"
              >
                {actor.video_url}
              </a>
            </CardContent>
          </Card>
        )}

        {/* Customer Reviews */}
        {actor.customer_reviews && actor.customer_reviews.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Star className="w-5 h-5 text-amber-500" />
                Customer Reviews
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {actor.customer_reviews.map((review, index) => (
                  <div key={index} className="border-l-4 border-amber-400 pl-4 py-2">
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <div className="flex items-center gap-2">
                        <div className="flex">
                          {Array.from({ length: 5 }, (_, i) => (
                            <Star 
                              key={i} 
                              className={`w-4 h-4 ${i < (parseInt(review.rating) || 0) ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`} 
                            />
                          ))}
                        </div>
                        <span className="font-semibold">{review.customer_name || review.name}</span>
                        {review.position && review.company && (
                          <span className="text-sm text-muted-foreground">
                            - {review.position}, {review.company}
                          </span>
                        )}
                      </div>
                      {review.date && (
                        <span className="text-xs text-muted-foreground">{review.date}</span>
                      )}
                    </div>
                    <p className="text-slate-700">{review.comment || review.review}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Guard Rail Settings */}
        <Card className="border-2 border-indigo-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="w-5 h-5 text-indigo-600" />
              Guard Rail Settings
            </CardTitle>
            <CardDescription>
              Expertise boundary protection configuration
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Status</p>
                <Badge variant={actor.guard_rail_enabled ? "default" : "secondary"}>
                  {actor.guard_rail_enabled ? "Enabled" : "Disabled"}
                </Badge>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Strictness</p>
                <p className="font-medium capitalize">{actor.guard_rail_strictness || "moderate"}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Model</p>
                <p className="text-sm">{actor.guard_rail_model || "google/gemini-2.0-flash-exp:free"}</p>
              </div>
            </div>
            {actor.custom_refusal_message && (
              <div>
                <p className="text-sm text-muted-foreground">Custom Refusal Message</p>
                <p className="text-sm bg-slate-50 p-2 rounded">{actor.custom_refusal_message}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Technical Configuration */}
        <Card className="border-2 border-purple-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-purple-600" />
              Technical Configuration
            </CardTitle>
            <CardDescription>
              AI model and system settings
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {actor.default_llm_model && (
              <div>
                <p className="text-sm text-muted-foreground">Default LLM Model</p>
                <p className="font-medium">{actor.default_llm_model}</p>
              </div>
            )}
            
            {/* Fallback Models Section - Always show with message if empty */}
            <div>
              <p className="text-sm text-muted-foreground">Fallback Model Chain</p>
              {actor.fallback_models && Array.isArray(actor.fallback_models) && actor.fallback_models.length > 0 ? (
                <>
                  <div className="space-y-2 mt-2">
                    {actor.fallback_models.map((model, index) => (
                      <div key={index} className="flex items-center gap-2 text-sm">
                        <Badge variant="outline" className="text-xs">
                          Priority {index + 1}
                        </Badge>
                        <span className="font-medium">{model}</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    If primary model fails, system will automatically try these models in order
                  </p>
                </>
              ) : (
                <p className="text-sm text-muted-foreground mt-1 italic">
                  No fallback models configured. Edit this actor to add fallback models for automatic fail-over.
                </p>
              )}
            </div>
            
            {actor.system_prompt && (
              <div>
                <p className="text-sm text-muted-foreground">System Prompt</p>
                <p className="text-sm bg-slate-50 p-3 rounded whitespace-pre-wrap">{actor.system_prompt}</p>
              </div>
            )}
            {!actor.default_llm_model && !actor.fallback_models?.length && !actor.system_prompt && (
              <p className="text-sm text-muted-foreground">No technical configuration set</p>
            )}
          </CardContent>
        </Card>

        {/* Embed Script Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Code className="w-5 h-5 text-indigo-600" />
              Website Embed Script
            </CardTitle>
            <CardDescription>
              Add this AI Actor chat widget to any website. Uses your brand colors and name from Settings → Branding.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Branding Preview */}
            {branding && (
              <div className="p-4 bg-slate-50 rounded-lg border space-y-2">
                <p className="text-sm font-semibold text-slate-700">Widget Branding:</p>
                <div className="flex items-center gap-3 text-sm">
                  <span className="text-slate-600">Powered by:</span>
                  <span className="font-semibold">{branding.brand_name || "AI Assistant"}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-slate-600">Colors:</span>
                  <div className="flex gap-2">
                    <div 
                      className="w-8 h-8 rounded border-2 border-slate-200" 
                      style={{ backgroundColor: branding.primary_color }}
                      title="Primary"
                    />
                    <div 
                      className="w-8 h-8 rounded border-2 border-slate-200" 
                      style={{ backgroundColor: branding.secondary_color }}
                      title="Secondary"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Embed Script */}
            <div className="relative">
              <div className="bg-slate-900 text-slate-100 p-4 rounded-lg overflow-x-auto text-xs font-mono max-h-64 overflow-y-auto">
                <pre className="whitespace-pre-wrap">{embedScript}</pre>
              </div>
              <Button
                onClick={copyEmbedScript}
                className="absolute top-2 right-2"
                size="sm"
                variant="secondary"
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4 mr-2" />
                    Copy Script
                  </>
                )}
              </Button>
            </div>

            {/* Instructions */}
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 space-y-2">
              <p className="text-sm font-semibold text-blue-900">How to use:</p>
              <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                <li>Copy the embed script above</li>
                <li>Paste it into your website's HTML, just before the closing <code className="bg-blue-100 px-1 rounded">&lt;/body&gt;</code> tag</li>
                <li>The chat widget will appear on the bottom-right corner of your website</li>
                <li>Customize branding in <strong>Settings → Branding</strong></li>
              </ol>
            </div>
          </CardContent>
        </Card>

        {/* Coaching Notes */}
        <Card className="border-2 border-emerald-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-emerald-600" />
              Coaching Notes / Memory Bank (Admin Only)
            </CardTitle>
            <CardDescription>
              These notes automatically guide this AI Actor's responses
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loadingNotes ? (
              <p className="text-sm text-muted-foreground">Loading coaching notes...</p>
            ) : coachingNotes.length === 0 ? (
              <div className="p-4 border rounded-lg bg-emerald-50 text-center">
                <p className="text-sm text-muted-foreground">
                  No coaching notes yet. Add notes from Chat Moderation to improve this AI Actor's responses.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {Object.entries(
                  coachingNotes.reduce((acc, note) => {
                    const cat = note.category || 'Other';
                    if (!acc[cat]) acc[cat] = [];
                    acc[cat].push(note);
                    return acc;
                  }, {})
                ).map(([category, notes]) => (
                  <div key={category} className="border rounded-lg p-4 bg-emerald-50">
                    <h4 className="font-semibold text-sm text-emerald-900 mb-3 uppercase tracking-wide">
                      {category}
                    </h4>
                    <div className="space-y-2">
                      {notes.map((note) => (
                        <div key={note.id} className="flex items-start justify-between gap-2 p-3 bg-white rounded border">
                          <div className="flex-1">
                            <p className="text-sm text-slate-700">{note.content}</p>
                            <p className="text-xs text-muted-foreground mt-2">
                              Added by {note.created_by} on {new Date(note.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteCoachingNote(note.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            Delete
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
            <p className="text-xs text-muted-foreground mt-4">
              💡 To add new coaching notes, go to Chat Moderation → View a conversation → Add Coaching Note
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
